import { lazy } from "react";
import { AppShell, createTheme, MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { Redirect, Route, Switch, Router } from "wouter";
import { ErrorBoundary } from "@sentry/react";

import Confirmation from "../../Boilerplate/Auth/Confirmation";
import ForgotPassword from "../../Boilerplate/Auth/ForgotPassword";
import LoginForm from "../../Boilerplate/Auth/Login";
import LoginLinkHandler from "../../Boilerplate/Auth/LoginLink";
import ResetPassword from "../../Boilerplate/Auth/ResetPassword";
import ProfileEdit from "../../Boilerplate/Profile/ProfileEdit";
import UserList from "../../Boilerplate/Users/UserList";
import UserAdd from "../../Boilerplate/Users/UserAdd";
import UserEdit from "../../Boilerplate/Users/UserEdit";
import TenantList from "../../Boilerplate/AdminTenants/TenantList";
import TenantEdit from "./AdminTenants/TenantEdit";
import TenantCreate from "../../Boilerplate/AdminTenants/TenantCreate";
import TenantUserCreate from "../../Boilerplate/Tenants/TenantUserCreate";
import TenantSettings from "./Tenants/TenantSettings";
import AdminUsersList from "../../Boilerplate/AdminUsers/AdminUsersList";
import AdminUserDetails from "../../Boilerplate/AdminUsers/AdminUserDetails";
import JobDetailsAdmin from "../../Boilerplate/AdminJobs/JobDetails";
import JobListAdmin from "../../Boilerplate/AdminJobs/Jobs";
import ChangePassword from "../../Boilerplate/Auth/ChangePassword";
import RegisterForm from "../../Boilerplate/Auth/Register";
import UserDetails from "../../Boilerplate/Users/UserDetails";
import InvitationForm from "../../Boilerplate/Auth/Invitation";

import { NBoilerplateContext } from "../../Boilerplate/NBoilerplateContext";

import ErrorFallback from "../../Boilerplate/_Shared/ErrorFallback";
import Home from "./Home";
import JobStatus from "./Jobs/Status";
import ProductsList from "./Products/List";
import ProductTypesList from "./ProductTypes/List";
import ProductDetails from "./Products/Details";
import ProductTypesTree from "./ProductTypes/Tree";
import ProductTypeDetails from "./ProductTypes/Details";
import { HeaderMenu } from "./HeaderMenu";
import MarketplaceList from "./Marketplaces/MarketplaceList";
import MarketplaceEdit from "./Marketplaces/MarketplaceEdit";
import AbstractMappingEdit from "./AbstractMapping/AbstractMappingEdit";
import PaymentSuccess from "./Payment/Success";
import PaymentCancelled from "./Payment/Cancelled";
import { CreditsPricingPage } from "./Payment/CreditsPricingPage";
import ValueListDetails from "./ProductTypes/ValueLists/Details";
import UploadPage from "./Imports/UploadPage";
import ImportList from "./Imports/ImportList";
import ImportDetails from "./Imports/ImportDetails";
import MappingDetails from "./Mappings/MappingDetails";
import { useDisclosure } from "@mantine/hooks";
import Navbar from "./NavBar";
import SignupCodeList from "./AdminSignupCodes/SignupCodeList";
import SignupCodeEdit from "./AdminSignupCodes/SignupCodeEdit";
import PartnerList from "./AdminPartners/PartnerList";
import PartnerCreate from "./AdminPartners/PartnerCreate";
import PartnerHome from "./Partner/PartnerHome";
import PartnerCodeList from "./Partner/PartnerCodeList";
import PartnerCodeEdit from "./Partner/PartnerCodeEdit";
import PartnerMerchantList from "./Partner/PartnerMerchantList";
import PartnerMerchantCreate from "./Partner/PartnerMerchantCreate";
import PartnerPlaybook from "./Partner/PartnerPlaybook";
import RequirePartner from "./Partner/RequirePartner";
import { RootRedirect } from "./authRouting";

import { store, useStore } from "./store";
// Hidden: PIM, Esska, and Translation features have moved to FacetFlux
// const EsskaProductList = lazy(() =>
//   import("./Products/Esska/EsskaProductList.jsx")
// );
// const EsskaProductDetails = lazy(() =>
//   import("./Products/Esska/EsskaProductDetails.jsx")
// );
// const TranslationList = lazy(() =>
//   import("./Translations/TranslationList.jsx")
// );
// const TranslationDetails = lazy(() =>
//   import("./Translations/TranslationDetails.jsx")
// );
//
// const SchemaList = lazy(() => import("./Pim/Schemas/SchemaList"));
// const SchemaEditor = lazy(() => import("./Pim/Schemas/SchemaEditor"));
// const ProductTypeDetail = lazy(() =>
//   import("./Pim/ProductTypes/ProductTypeDetail")
// );
// const PimProductList = lazy(() => import("./Pim/Products/PimProductList"));
// const PimProductDetails = lazy(() =>
//   import("./Pim/Products/PimProductDetails")
// );

function App() {
  const profile = useStore((state) => state.profile);
  const [opened, { toggle }] = useDisclosure(false);

  const optivise = [
    "#e4faff",
    "#d2effb",
    "#a8ddf3",
    "#7ac9eb",
    "#55b9e4",
    "#3eafe0",
    "#2eaae0",
    "#1b90c1",
    "#0284b3",
    "#00739f",
  ];

  const theme = createTheme({
    primaryColor: "optivise",
    colors: {
      optivise,
    },
    components: {
      Container: {
        defaultProps: {
          size: "xl",
        },
      },
    },
  });

  return (
    <Router>
      <NBoilerplateContext.Provider value={store}>
        <MantineProvider theme={theme} defaultColorScheme="auto">
          <ModalsProvider>
            <Notifications />
            <AppShell
              header={{ height: 60 }}
              padding="xs"
              navbar={{
                width: 220,
                breakpoint: "sm",
                collapsed: { mobile: !opened, desktop: true },
              }}
            >
              <AppShell.Header>
                <ErrorBoundary fallback={ErrorFallback}>
                  <HeaderMenu opened={opened} toggle={toggle} />
                </ErrorBoundary>
              </AppShell.Header>
              <AppShell.Navbar p={0}>
                <ErrorBoundary fallback={ErrorFallback}>
                  <Navbar toggle={toggle} />
                </ErrorBoundary>
              </AppShell.Navbar>
              <AppShell.Main>
                <ErrorBoundary fallback={ErrorFallback}>
                  <Switch>
                    <Route path="/home" component={Home} />
                    <Route path="/products" component={ProductsList} />
                    <Route path="/products/:id" component={ProductDetails} />

                    <Route
                      path="/marketplaces/:marketplaceId/types/tree"
                      component={ProductTypesTree}
                    />
                    <Route
                      path="/marketplaces/:marketplaceId/types/:id"
                      component={ProductTypeDetails}
                    />
                    <Route
                      path="/marketplaces/:marketplaceId/types"
                      component={ProductTypesList}
                    />

                    <Route
                      path="/marketplaces/:marketplaceId/lists/:listCode"
                      component={ValueListDetails}
                    />

                    <Route path="/imports/upload" component={UploadPage} />

                    <Route path="/imports" component={ImportList} />
                    <Route path="/imports/:id" component={ImportDetails} />
                    <Route
                      path="/imports/:importId/mappings/:mappingId/products"
                      component={ProductsList}
                    />
                    <Route
                      path="/imports/:importId/mappings/:mappingId"
                      component={MappingDetails}
                    />

                    {/* Hidden: PIM, Esska, and Translation features have moved to FacetFlux */}
                    {/* <Route
                      path="/esska/products"
                      component={EsskaProductList}
                    />
                    <Route
                      path="/esska/products/:id"
                      component={EsskaProductDetails}
                    /> */}

                    {/* <Route path="/pim/schemas" component={SchemaList} />
                    <Route path="/pim/schemas/:id" component={SchemaEditor} />
                    <Route
                      path="/pim/schemas/:schemaId/product-types/:productTypeId"
                      component={ProductTypeDetail}
                    />
                    <Route path="/pim/products" component={PimProductList} />
                    <Route
                      path="/pim/products/:id"
                      component={PimProductDetails}
                    /> */}

                    <Route path="/jobs/:jobId" component={JobStatus} />

                    {/* Boilerplate, User, Tenants etc. */}
                    <Route path={`/invite/:token`}>
                      {(params) => (
                        <InvitationForm appName="Optivise" params={params} />
                      )}
                    </Route>

                    <Route path={`/auth/reset`} component={ForgotPassword} />
                    <Route
                      path={`/auth/reset-password/:token`}
                      component={ResetPassword}
                    />
                    <Route path={`/auth/login`}>
                      <LoginForm realm="OPTU" />
                    </Route>
                    <Route
                      path={`/auth/login/:token`}
                      component={LoginLinkHandler}
                    />
                    <Route
                      path={`/auth/confirm-email/:token`}
                      component={Confirmation}
                    />

                    <Route
                      path={`/auth/change-password`}
                      component={ChangePassword}
                    />
                    <Route path={`/auth/register`}>
                      <RegisterForm
                        realm="OPTU"
                        touLink="https://www.optivise.io/legal"
                      />
                    </Route>

                    <Route path={`/profile`} component={ProfileEdit} />
                    <Route path={`/plans`}>
                      <Redirect to="/credits" />
                    </Route>
                    <Route path={`/credits`} component={CreditsPricingPage} />
                    <Route
                      path="/payment/completed"
                      component={PaymentSuccess}
                    />
                    <Route
                      path="/payment/cancelled"
                      component={PaymentCancelled}
                    />

                    <Route path="/partner/codes/create">
                      <RequirePartner>
                        <PartnerCodeEdit />
                      </RequirePartner>
                    </Route>
                    <Route path="/partner/codes/:id">
                      {(params) => (
                        <RequirePartner>
                          <PartnerCodeEdit params={params} />
                        </RequirePartner>
                      )}
                    </Route>
                    <Route path="/partner/codes">
                      <RequirePartner>
                        <PartnerCodeList />
                      </RequirePartner>
                    </Route>
                    <Route path="/partner/merchants/create">
                      <RequirePartner>
                        <PartnerMerchantCreate />
                      </RequirePartner>
                    </Route>
                    <Route path="/partner/merchants">
                      <RequirePartner>
                        <PartnerMerchantList />
                      </RequirePartner>
                    </Route>
                    <Route path="/partner/playbook">
                      <RequirePartner>
                        <PartnerPlaybook />
                      </RequirePartner>
                    </Route>
                    <Route path="/partner">
                      <RequirePartner>
                        <PartnerHome />
                      </RequirePartner>
                    </Route>

                    {/* TENANT OWNER STUFF */}
                    {(profile?.role == "TenantOwner" ||
                      profile?.role == "TenantAdmin") && (
                      <>
                        <Route
                          path={`/settings/tenant`}
                          component={TenantSettings}
                        />

                        <Route path={`/users`} component={UserList} />
                        <Route path={`/users/add`}>
                          <UserAdd realm="OPTU" />
                        </Route>
                        <Route
                          path={`/users/details/:id`}
                          component={UserDetails}
                        />
                        <Route path={`/users/edit/:id`} component={UserEdit} />
                      </>
                    )}

                    {/* ADMIN STUFF */}
                    {profile?.role == "GlobalAdmin" && (
                      <>
                        <Route path="/admin/jobs" component={JobListAdmin} />
                        <Route
                          path="/admin/jobs/:id"
                          component={JobDetailsAdmin}
                        />

                        {/* <Route
                          path="/translations"
                          component={TranslationList}
                        />
                        <Route
                          path="/translations/:id"
                          component={TranslationDetails}
                        /> */}

                        <Route
                          path="/marketplaces"
                          component={MarketplaceList}
                        />
                        <Route
                          path="/marketplaces/create"
                          component={MarketplaceEdit}
                        />
                        <Route
                          path="/marketplaces/:id"
                          component={MarketplaceEdit}
                        />

                        <Route
                          path={`/marketplaces/:marketplaceId/mapping`}
                          component={AbstractMappingEdit}
                        />

                        <Route path={`/tenants`} component={TenantList} />
                        <Route
                          path={`/tenants/create`}
                          component={TenantCreate}
                        />
                        <Route path={`/tenants/:id`} component={TenantEdit} />

                        <Route
                          path={`/tenants/:tenantId/users/create`}
                          component={TenantUserCreate}
                        />
                        <Route
                          path="/admin/partners/create"
                          component={PartnerCreate}
                        />
                        <Route
                          path="/admin/partners"
                          component={PartnerList}
                        />

                        <Route
                          path="/admin/signup-codes"
                          component={SignupCodeList}
                        />
                        <Route
                          path="/admin/signup-codes/create"
                          component={SignupCodeEdit}
                        />
                        <Route
                          path="/admin/signup-codes/:id"
                          component={SignupCodeEdit}
                        />

                        <Route
                          path={`/admin/users`}
                          component={AdminUsersList}
                        />
                        <Route
                          path={`/admin/users/:userId`}
                          component={AdminUserDetails}
                        />
                      </>
                    )}

                    <Route path="/" component={RootRedirect} />
                    {/* Defensive: singular /user is not a real route; send legacy bookmarks to login */}
                    <Route path="/user">
                      <Redirect to="/auth/login" />
                    </Route>
                    <Route>404, Not Found!</Route>
                  </Switch>
                </ErrorBoundary>
              </AppShell.Main>
            </AppShell>
          </ModalsProvider>
        </MantineProvider>
      </NBoilerplateContext.Provider>
    </Router>
  );
}

export default App;
